import React, { ReactElement, useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { NavigationContext } from '@context/NavigationContext'
import { Pages } from '@models/Pages'
import { SurveyStep } from '@models/Survey/SurveyStep'
import Button from '@components/Button/Button'
import MenuButton from './MenuButton'
import './WithMenu.scss'

import reset from '@assets/images/nav/reset-active.png'
import resetDisabled from '@assets/images/nav/reset-disabled.png'

import shareAndroidIcon from '@assets/images/icons/share/share-android.png'
import shareAndroidActiveIcon from '@assets/images/icons/share/share-android-active.png'

import shareIosIcon from '@assets/images/icons/share/share-ios.png'
import shareIosActiveIcon from '@assets/images/icons/share/share-ios-active.png'

import menu from '@assets/images/nav/menu.png'
import menuDisabled from '@assets/images/nav/menu-disabled.png'

import chevronPrev from '@assets/images/nav/chevron-prev.png'
import chevronPrevDisabled from '@assets/images/nav/chevron-prev-disabled.png'
import chevronNext from '@assets/images/nav/chevron-next.png'
import chevronNextDisabled from '@assets/images/nav/chevron-next-disabled.png'
import { useShare } from '@hooks/useShare'
import { isApplePlatform } from '@utils/platform'
import { isPathBrowsable } from '../../dev/devInspectorPlacement'

const MenuBar = (): ReactElement => {
  const {
    isPrevEnabled,
    isNextEnabled,
    isResetEnabled,
    isShareEnabled,
    prevHandler,
    nextHandler,
    resetHandler,
    shareHandler,
    setShareHandler,
    enableShare,
    disableShare,
  } = useContext(NavigationContext)

  const location = useLocation()
  const sandboxUrl = `/${Pages.Survey}/${SurveyStep.Sandbox}`

  const [prevImageSource, setPrevImageSource] = useState(chevronPrevDisabled)
  const [nextImageSource, setNextImageSource] = useState(chevronNextDisabled)
  const [resetImageSource, setResetImageSource] = useState(resetDisabled)
  const [shareImageSource, setShareImageSource] = useState(shareAndroidIcon)
  const [isInSandbox, setInSandbox] = useState(location.pathname.endsWith(sandboxUrl))
  const [isBrowsable, setIsBrowsable] = useState(isPathBrowsable(location.pathname))

  const { handleOpenShareModal } = useShare()

  useEffect(() => {
    setInSandbox(location.pathname.endsWith(sandboxUrl))
    setIsBrowsable(isPathBrowsable(location.pathname))
  }, [location])

  useEffect(() => {
    if (isInSandbox) {
      setPrevImageSource(isPrevEnabled ? menu : menuDisabled)
      setShareHandler(() => handleOpenShareModal())
      enableShare?.()
    } else {
      setPrevImageSource(isPrevEnabled ? chevronPrev : chevronPrevDisabled)
      disableShare?.()
    }
  }, [isInSandbox, isPrevEnabled])

  useEffect(() => {
    if (isInSandbox) {
      setNextImageSource(isNextEnabled ? menu : menuDisabled)
    } else {
      setNextImageSource(isNextEnabled ? chevronNext : chevronNextDisabled)
    }
  }, [isInSandbox, isNextEnabled])

  useEffect(() => {
    setResetImageSource(isResetEnabled ? reset : resetDisabled)
  }, [isInSandbox, isResetEnabled])

  useEffect(() => {
    const shareIcon = isApplePlatform ? shareIosIcon : shareAndroidIcon
    const shareActiveIcon = isApplePlatform ? shareIosActiveIcon : shareAndroidActiveIcon

    setShareImageSource(isShareEnabled ? shareActiveIcon : shareIcon)
  }, [isInSandbox, isShareEnabled])

  // http://reactcommunity.org/react-transition-group/transition#Transition-prop-nodeRef
  const prevButton = isInSandbox ? (
    <Button
      propClassName="Button-nav sandboxed"
      icon={<img src={resetImageSource} />}
      disabled={!isResetEnabled}
      onClick={resetHandler}
    />
  ) : (
    <Button
      propClassName="Button-nav"
      icon={<img src={prevImageSource} />}
      disabled={!isPrevEnabled}
      onClick={prevHandler}
    />
  )

  const nextButton = isInSandbox ? (
    <React.Fragment>
      <Button
        propClassName="Button-nav sandboxed"
        icon={<img src={shareImageSource} />}
        disabled={!isShareEnabled}
        onClick={shareHandler}
      />
    </React.Fragment>
  ) : (
    <Button
      propClassName="Button-nav"
      icon={<img src={nextImageSource} />}
      disabled={!isNextEnabled}
      onClick={nextHandler}
    />
  )

  return (
    <nav className="Menu-Bar">
      {isBrowsable && prevButton}
      {isBrowsable ? (
        <div className="Menu-Bar-burgerStack">
          <MenuButton />
        </div>
      ) : (
        <MenuButton />
      )}
      {isBrowsable && nextButton}
    </nav>
  )
}
export default MenuBar
